import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { radius } from '@karehero/llama/theme/radius';

import { Box, Stack } from '@mui/material';

import ComputerFrame from '@/designs-dev/device/ComputerFrame';
import ScaleToFit from '@/designs-dev/device/ScaleToFit';
import { PhoneScreenContext } from '@/designs-dev/device/PhoneScreenContext';
import {
  computerScreenHeight,
  computerScreenWidth,
  type Viewport,
} from '@/designs-dev/device/viewport';
import ControlPanel from '@/cam-admin/control-panel/ControlPanel';
import { shortlistScenes } from '@/cam-admin/paywall/Shortlist.scenes';
import {
  InspectProvider,
  useInspect,
} from '@/designs-dev/inspect/InspectContext';
import InspectOverlay from '@/designs-dev/inspect/InspectOverlay';
import InspectPanel from '@/designs-dev/inspect/InspectPanel';
import LayersPanel from '@/designs-dev/inspect/LayersPanel';
import DrillModeToggle from '@/designs-dev/inspect/DrillModeToggle';
import StageBar, { STAGE_BAR_ROOM } from '@/designs-dev/inspect/StageBar';
import StateSelector from '@/designs-dev/inspect/StateSelector';
import { useScreenSelect } from '@/designs-dev/loft/ScreenSelectContext';

// The CAM paywall shortlist on a centred stage inside the brand laptop frame,
// matching how the other CAM prototypes sit. Chrome is prototype-only: drill
// toggle, viewport switch and state select float in a bar at the foot of the
// stage, inspect panel docked beside it.
//
// Single page, so this is CareSearchStage without the page switching.
const Stage = () => {
  const { active, current, setActive, setCurrent, setHovered } = useInspect();
  const screenSelect = useScreenSelect();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [sceneId, setSceneId] = useState(
    () => searchParams.get('scene') ?? shortlistScenes[0].id,
  );

  // Clamp to a scene that exists, so a stale or hand-typed ?scene never breaks
  // the page.
  const currentScene =
    shortlistScenes.find((s) => s.id === sceneId) ?? shortlistScenes[0];

  // Reflect the active scene in the URL so a state is a shareable address.
  useEffect(() => {
    if (searchParams.get('scene') !== currentScene.id) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('scene', currentScene.id);
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScene.id]);

  // A web design opens at the laptop screen's width with no laptop drawn round
  // it: same screen as the frame, judged at the size it renders. The frame is
  // still a click away in the toggle for showing the thing off.
  const [viewport, setViewport] = useState<Viewport>(computerScreenWidth);
  const framed = viewport === 'frame';
  const [fullEl, setFullEl] = useState<HTMLElement | null>(null);

  // Esc is staged: a first press clears a selected layer, a second leaves drill
  // mode. Matches the other stages.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (current) {
        setCurrent(null);
        setHovered(null);
      } else {
        setActive(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, current, setActive, setCurrent, setHovered]);

  // Switching state clears any pinned component, so a ?select doesn't linger onto
  // the new (remounted) scene's nodes.
  const pickScene = (id: string) => {
    setCurrent(null);
    setHovered(null);
    setSceneId(id);
  };

  // The shortlist is a tab in the care circle, so the prototype is the control
  // panel opened on that tab — the same workspace the CAM is already in, rather
  // than a page of its own.
  //
  // key remounts the prototype in the chosen state, which also resets any
  // clicking-through the reader has done, so a scene always starts where it says.
  const page = (
    <ControlPanel
      key={currentScene.id}
      embedded
      initialTab='shortlist'
      withShortlist
      shortlist={currentScene.initial}
      onNavigate={(path) => navigate(path)}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: active ? 'custom.charcoal' : 'custom.neutral.100',
        transition: 'background-color 400ms ease',
      }}
    >
      {/* The stage, with the controls floating over its foot. */}
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            p: 3,
            // Room for the floating controls under the screen.
            pb: STAGE_BAR_ROOM,
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: framed ? 'center' : 'stretch',
              justifyContent: 'center',
            }}
          >
            {/* The Stack must span the full stage width — the laptop measures its
              wrapper to size itself, and an auto-width parent would make it
              measure (and feed back on) its own width. */}
            <Stack sx={{ alignItems: 'center', width: '100%', minHeight: 0 }}>
              {framed ? (
                <ComputerFrame>
                  {page}
                  <InspectOverlay />
                </ComputerFrame>
              ) : (
                /* Out of the frame. 'fill' takes the stage's own width and
                 height; a fixed width lays out at that width and is scaled
                 down to whatever room there is, so a 1440 design is judged at
                 1440 without running off the side of the stage. */
                <ScaleToFit
                  width={viewport === 'fill' ? 'fill' : viewport}
                  height={
                    viewport === 'fill' ? undefined : computerScreenHeight
                  }
                >
                  <Box
                    sx={{
                      position: 'relative',
                      height: '100%',
                      width: '100%',
                      overflow: 'hidden',
                      borderRadius: `${radius.md}px`,
                      border: 1,
                      borderColor: 'custom.neutral.200',
                      bgcolor: 'background.default',
                    }}
                    ref={(el: HTMLElement | null) => setFullEl(el)}
                  >
                    <PhoneScreenContext.Provider value={fullEl}>
                      {page}
                      <InspectOverlay />
                    </PhoneScreenContext.Provider>
                  </Box>
                </ScaleToFit>
              )}
            </Stack>
          </Box>
        </Box>

        {/* How you are looking at the design, whose view of the flow it is,
            then what that screen is showing. The middle one is only there when
            this screen is one of several — the route builds it, because it is
            the route that knows the siblings. */}
        <StageBar>
          <DrillModeToggle viewport={viewport} onViewportChange={setViewport} />
          {screenSelect}
          <StateSelector
            scenes={shortlistScenes}
            sceneId={currentScene.id}
            onSceneChange={pickScene}
          />
        </StageBar>
      </Box>

      <LayersPanel />
      <InspectPanel />
    </Box>
  );
};

const ShortlistStage = () => (
  <InspectProvider>
    <Stage />
  </InspectProvider>
);

export default ShortlistStage;
